import { Menu, Tabs } from '@recruit-robin/robin-theme/web-components';
import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import type { Vacancy } from 'clients/AutoCompleteClient/types';
import type { TabProps } from './Tab.types';

import { VacancyList } from 'components/ui/molecules/VacancyList/VacancyList';
import { logger } from 'config/logger';
import { useEnrichedVacanciesQuery } from 'hooks/reachout/useEnrichedVacanciesQuery';
import { useIndeedJobListQuery } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedJobListQuery';
import { getVacancyName } from 'utils/vacancyEnrichedUtils';
import { VacancyListTab } from '../../VacancyList/VacancyList.types';
import { HighQualityJobsMessageInfo } from '../HighQualityJobsMessageInfo/HighQualityJobsMessageInfo';
import { useStyles } from './IndeedTab.styles';

const IndeedTabContent = ({
  onCreateJob,
  onSelectVacancy,
  onEditVacancy,
  onDeleteVacancy,
  selectedVacancy,
  search = '',
  hideMessageInfo = false,
}: TabProps) => {
  const classes = useStyles({ contained: false });
  const [searchDebounced, setSearchDebounced] = useState(search);
  const { data: enrichedIndeedVacancyList = [], isFetching: isIndeedVacanciesFetching } = useEnrichedVacanciesQuery({
    search: searchDebounced,
    indeedOnly: true,
  });
  const isSearchLoading = isIndeedVacanciesFetching || search !== searchDebounced;

  useDebounce(() => setSearchDebounced(search), 230, [search]);

  const { data: indeedJobs, isLoading: isIndeedJobsLoading } = useIndeedJobListQuery();

  const indeedJobsFiltered = useMemo(() => {
    return indeedJobs.filter((job) => job.name.toLowerCase().includes(searchDebounced.toLowerCase()));
  }, [indeedJobs, searchDebounced]);

  const indeedVacancyList = useMemo<Vacancy[]>(() => {
    const indeedVacancyList = [...enrichedIndeedVacancyList];
    const indeedVacancyListKeys = indeedVacancyList.map((job) => job.job_indeed_id);

    indeedJobsFiltered.forEach((job) => {
      if (job.key) {
        if (!indeedVacancyListKeys.includes(job.key)) {
          indeedVacancyList.push({
            name: job.name,
            value: job.key,
            job_indeed_id: job.key,
            origin_job_title: job.name,
            type: 'INDEED',
            originalIndeedJob: job,
          });
        }
      } else {
        logger.error(`Indeed job key is not defined: ${JSON.stringify(job)}`);
      }
    });

    return indeedVacancyList.map((job) => {
      if (job.type === 'INDEED' && indeedVacancyListKeys.includes(job.value)) {
        const indedJob = indeedJobsFiltered.find((j) => j.key === job.value);

        return { ...job, originalIndeedJob: indedJob, name: getVacancyName(job) };
      }

      return { ...job, name: getVacancyName(job) };
    });
  }, [indeedJobsFiltered, enrichedIndeedVacancyList]);

  return (
    <>
      {!hideMessageInfo && (
        <div css={classes.messageInfo}>
          <HighQualityJobsMessageInfo />
        </div>
      )}
      {isSearchLoading ? (
        Array.from({ length: 4 }).map((_, i) => <Menu.Item.Skeleton key={i} />)
      ) : (
        <>
          <VacancyList
            onCreateJob={onCreateJob}
            selectedVacancy={selectedVacancy}
            onSelectVacancy={onSelectVacancy}
            onDeleteVacancy={onDeleteVacancy}
            onEditVacancy={onEditVacancy}
            vacanciesList={indeedVacancyList}
          />
          {isIndeedJobsLoading && Array.from({ length: 4 }).map((_, i) => <Menu.Item.Skeleton key={i} />)}
        </>
      )}
    </>
  );
};

export const IndeedTab = (props: TabProps) => {
  const classes = useStyles({ contained: false });

  return (
    <Tabs.Content customCss={classes.tabsContent} value={VacancyListTab.IndeedJobs}>
      <IndeedTabContent {...props} />
    </Tabs.Content>
  );
};
